import apiClient from "./apiClient";

export interface CategoryPricingRule {
  id: string;
  company_id: string;
  price_grid_id: string;
  item_id?: string | null;
  category_id?: string | null;
  customer_type_id?: string | null;
  unit_price_minor: number;
  effective_from?: string | null;
  effective_to?: string | null;
  price_type?: string;
  currency?: string;
  created_at?: string;
  updated_at?: string;
}

export interface CategoryPricingRuleUpsertPayload {
  price_grid_id: string;
  customer_type_id?: string | null;
  unit_price_minor: number;
  effective_from?: string | null;
  effective_to?: string | null;
  price_type?: string;
  currency?: string;
}

export type ItemPricingRule = CategoryPricingRule;

export interface ItemPricingRuleUpsertPayload {
  price_grid_id: string;
  customer_type_id?: string | null;
  unit_price_minor: number;
  effective_from?: string | null;
  effective_to?: string | null;
  price_type?: string;
  currency?: string;
}

export interface ItemPricingRulePatchPayload {
  price_grid_id?: string;
  customer_type_id?: string | null;
  unit_price_minor?: number;
  effective_from?: string | null;
  effective_to?: string | null;
  price_type?: string;
  currency?: string;
}

export interface ResolvedItemPrice {
  resolved_from:
    | "article_grid"
    | "category_grid"
    | "article_base"
    | "category_base";
  unit_price_minor: number;
  currency: string;
  rule_id?: string | null;
}

export interface CategoryPricingRulePatchPayload {
  price_grid_id?: string;
  customer_type_id?: string | null;
  unit_price_minor?: number;
  effective_from?: string | null;
  effective_to?: string | null;
  price_type?: string;
  currency?: string;
}

export async function listCategoryPricingRules(
  companyId: string,
  categoryId: string,
): Promise<CategoryPricingRule[]> {
  const response = await apiClient.get(
    `/api/companies/${companyId}/categories/${categoryId}/pricing-rules`,
  );

  return (response.pricingRules || []) as CategoryPricingRule[];
}

export async function createCategoryPricingRule(
  companyId: string,
  categoryId: string,
  payload: CategoryPricingRuleUpsertPayload,
): Promise<CategoryPricingRule> {
  const response = await apiClient.post(
    `/api/companies/${companyId}/categories/${categoryId}/pricing-rules`,
    payload,
  );

  return response.pricingRule as CategoryPricingRule;
}

export async function updateCategoryPricingRule(
  companyId: string,
  categoryId: string,
  pricingRuleId: string,
  payload: CategoryPricingRulePatchPayload,
): Promise<CategoryPricingRule> {
  const response = await apiClient.put(
    `/api/companies/${companyId}/categories/${categoryId}/pricing-rules/${pricingRuleId}`,
    payload,
  );

  return response.pricingRule as CategoryPricingRule;
}

export async function deleteCategoryPricingRule(
  companyId: string,
  categoryId: string,
  pricingRuleId: string,
): Promise<CategoryPricingRule> {
  const response = await apiClient.delete(
    `/api/companies/${companyId}/categories/${categoryId}/pricing-rules/${pricingRuleId}`,
  );

  return response.pricingRule as CategoryPricingRule;
}

export async function listItemPricingRules(
  companyId: string,
  itemId: string,
): Promise<ItemPricingRule[]> {
  const response = await apiClient.get(
    `/api/companies/${companyId}/items/${itemId}/pricing-rules`,
  );

  return (response.pricingRules || []) as ItemPricingRule[];
}

export async function createItemPricingRule(
  companyId: string,
  itemId: string,
  payload: ItemPricingRuleUpsertPayload,
): Promise<ItemPricingRule> {
  const response = await apiClient.post(
    `/api/companies/${companyId}/items/${itemId}/pricing-rules`,
    payload,
  );

  return response.pricingRule as ItemPricingRule;
}

export async function updateItemPricingRule(
  companyId: string,
  itemId: string,
  pricingRuleId: string,
  payload: ItemPricingRulePatchPayload,
): Promise<ItemPricingRule> {
  const response = await apiClient.put(
    `/api/companies/${companyId}/items/${itemId}/pricing-rules/${pricingRuleId}`,
    payload,
  );

  return response.pricingRule as ItemPricingRule;
}

export async function deleteItemPricingRule(
  companyId: string,
  itemId: string,
  pricingRuleId: string,
): Promise<ItemPricingRule> {
  const response = await apiClient.delete(
    `/api/companies/${companyId}/items/${itemId}/pricing-rules/${pricingRuleId}`,
  );

  return response.pricingRule as ItemPricingRule;
}

export async function resolveItemPrice(
  companyId: string,
  itemId: string,
  options: {
    priceGridId?: string;
    customerTypeId?: string;
    effectiveDate?: string;
  } = {},
): Promise<ResolvedItemPrice | null> {
  const params = new URLSearchParams();
  params.append("itemId", itemId);
  if (options.priceGridId) params.append("priceGridId", options.priceGridId);
  if (options.customerTypeId)
    params.append("customerTypeId", options.customerTypeId);
  if (options.effectiveDate)
    params.append("effectiveDate", options.effectiveDate);

  const response = await apiClient.get(
    `/api/companies/${companyId}/prices/resolve?${params.toString()}`,
  );

  return (response.price || null) as ResolvedItemPrice | null;
}

const pricingService = {
  listCategoryPricingRules,
  createCategoryPricingRule,
  updateCategoryPricingRule,
  deleteCategoryPricingRule,
  listItemPricingRules,
  createItemPricingRule,
  updateItemPricingRule,
  deleteItemPricingRule,
  resolveItemPrice,
};

export default pricingService;
